import { useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "usehooks-ts";

export type ScrollOrientation = "horizontal" | "vertical";

/**
 * Tracks how far a scrollable element has been scrolled along one axis — ref
 * to attach, whether it's at the start/end and whether it overflows at all.
 * Useful for driving dynamic affordances like edge shadows (see `ScrollArea`).
 */
export const useScrollBoundary = (
  orientation: ScrollOrientation = "horizontal",
) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  // layout effect, not a regular one — the initial measurement below has to
  // land before the browser paints, or the first frame renders with the
  // default (non-scrollable) state and the edge shadow visibly pops in a
  // moment later once the real measurement lands
  useIsomorphicLayoutEffect(() => {
    const scroller = ref.current;
    if (!scroller) return;

    const isHorizontal = orientation === "horizontal";
    const update = () => {
      const scrollPos = isHorizontal ? scroller.scrollLeft : scroller.scrollTop;
      const size = isHorizontal ? scroller.offsetWidth : scroller.offsetHeight;
      const contentSize = isHorizontal
        ? scroller.scrollWidth
        : scroller.scrollHeight;
      setIsStart(scrollPos <= 0);
      setIsEnd(Math.ceil(scrollPos) + size >= contentSize);
      setIsScrollable(contentSize > size);
    };

    // rAF-throttle so scroll + resize storms collapse to one measure per frame
    let frame = 0;
    const scheduleUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    // calculate initial values before any scroll event has happened
    update();
    scroller.addEventListener("scroll", scheduleUpdate);

    // watch the content too: adding/removing items changes scrollWidth/Height
    // without resizing the scroller itself
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(scroller);
    if (scroller.firstElementChild) {
      resizeObserver.observe(scroller.firstElementChild);
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [orientation]);

  return { ref, isStart, isEnd, isScrollable } as const;
};
